import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '../ui-pages.css';

const Tags = () => {
  const { workspaceUuid } = useParams();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tags?workspaceUuid=${workspaceUuid}`);
      setTags(res.data);
    } catch (err) {
      console.error('Failed to fetch tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTag) {
        await axios.put(`${API_URL}/api/tags/${editingTag._id}`, { name, description });
      } else {
        await axios.post(`${API_URL}/api/tags`, { workspaceUuid, name, description });
      }
      setShowModal(false);
      setName('');
      setDescription('');
      setEditingTag(null);
      fetchTags();
    } catch (err) {
      alert('Action failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this intent tag?')) {
      await axios.delete(`${API_URL}/api/tags/${id}`);
      fetchTags();
    }
  };

  const TagIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>;

  if (loading) return (
    <div className="ui-page">
      <div className="ui-head">
        <div>
          <div className="cl-skel" style={{ width: 200, height: 22, borderRadius: 999, marginBottom: 10 }} />
          <div className="cl-skel" style={{ width: 320, height: 12, borderRadius: 999 }} />
        </div>
      </div>
      <div className="ui-grid cl-skel-stagger">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="cl-skel-card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div className="cl-skel cl-skel-circle" style={{ width: 42, height: 42, flex: 'none' }} />
              <div className="cl-skel" style={{ width: '50%', height: 13, borderRadius: 999 }} />
            </div>
            <div className="cl-skel" style={{ width: '95%', height: 9, borderRadius: 999, marginBottom: 8 }} />
            <div className="cl-skel" style={{ width: '78%', height: 9, borderRadius: 999 }} />
            <div style={{ borderTop: '1px solid #F1ECE2', marginTop: 16, paddingTop: 14, display: 'flex', gap: 10 }}>
              <div className="cl-skel" style={{ width: 54, height: 12, borderRadius: 999 }} />
              <div className="cl-skel" style={{ width: 54, height: 12, borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="ui-page">
      <header className="ui-head">
        <div>
          <h1 className="ui-title">AI Intent Tags</h1>
          <p className="ui-sub">Define how Claude should categorize your customer messages.</p>
        </div>
        <button className="ui-btn" onClick={() => { setEditingTag(null); setName(''); setDescription(''); setShowModal(true); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          New Tag
        </button>
      </header>

      {tags.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic"><TagIcon /></div>
          <h2>No intent tags yet</h2>
          <p>Create tags so the AI can categorize incoming messages by intent.</p>
          <button className="ui-btn" style={{ margin: '0 auto' }} onClick={() => { setEditingTag(null); setShowModal(true); }}>New Tag</button>
        </div>
      ) : (
        <div className="ui-grid cl-stagger">
          {tags.map(tag => (
            <div key={tag._id} className="ui-card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#FAF7F1', color: '#111111', display: 'grid', placeItems: 'center', flex: 'none', boxShadow: 'inset 0 0 0 1px rgba(17,17,17,.05)' }}><TagIcon /></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em', color: '#111111' }}>{tag.name}</h3>
                  <span style={{ display: 'inline-block', marginTop: 5, fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: '#6B6862', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: 999, padding: '2px 9px' }}>{String(tag.name || '').toLowerCase().replace(/\s+/g, '_')}</span>
                </div>
              </div>
              <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6B6862', lineHeight: 1.55, flex: 1 }}>{tag.description || <span style={{ color: '#9C978C' }}>No description.</span>}</p>
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #F1ECE2', display: 'flex', gap: 8 }}>
                <button className="ui-btn-ghost ui-btn ui-btn-sm" onClick={() => { setEditingTag(tag); setName(tag.name); setDescription(tag.description); setShowModal(true); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                  Edit
                </button>
                <button className="ui-btn-danger ui-btn ui-btn-sm" onClick={() => handleDelete(tag._id)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(17,17,17,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#FFFFFF', width: '100%', maxWidth: '400px', borderRadius: '20px', padding: '32px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', letterSpacing: '-0.03em', color: '#111111', marginBottom: '24px' }}>{editingTag ? 'Edit Tag' : 'Add Tag'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3A3A37', marginBottom: '8px' }}>Tag Name (Intent)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Refund"
                  style={{ width: '100%', padding: '12px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', color: '#111111' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3A3A37', marginBottom: '8px' }}>Description (AI Instructions)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Use this tag when the customer asks for their money back or cancellation."
                  style={{ width: '100%', height: '100px', padding: '12px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', resize: 'none', color: '#111111' }}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '999px', border: '1px solid #E7E1D6', background: '#FFFFFF', color: '#3A3A37', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: '12px', borderRadius: '999px', border: 'none', background: '#111111', color: '#F9F4EE', fontWeight: '600', cursor: 'pointer', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>Save Tag</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tags;
